import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import useRealtimeNotifications from "../hooks/useRealtimeNotifications";
import Navbar from "../components/Navbar";
import AvatarLightbox from "../components/AvatarLightbox";
import FoundingBadge from "../components/FoundingBadge";
import { Input } from "../components/ui/input";
import { Users, Search, Building2, Sparkles, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const apiBase = process.env.REACT_APP_BACKEND_URL || "";

const LEVEL_STYLES = {
  "Einsteiger":             { hex: "#2ecc71", bg: "rgba(46,204,113,0.08)", border: "rgba(46,204,113,0.35)" },
  "Fortgeschritten":        { hex: "#4a90d9", bg: "rgba(74,144,217,0.08)", border: "rgba(74,144,217,0.35)" },
  "Profi":                  { hex: "#a855f7", bg: "rgba(168,85,247,0.08)", border: "rgba(168,85,247,0.35)" },
  "Dienstleister": { hex: "#f39c12", bg: "rgba(243,156,18,0.08)", border: "rgba(243,156,18,0.35)" },
};

const isOnline = (lastSeen) => {
  if (!lastSeen) return false;
  return (Date.now() - new Date(lastSeen).getTime()) < 5 * 60 * 1000;
};

function MemberAvatar({ member, onClick }) {
  const initials = `${(member.first_name?.[0] || "").toUpperCase()}${(member.last_name?.[0] || "").toUpperCase()}`;
  const src = member.avatar_url ? (member.avatar_url.startsWith("/") ? `${apiBase}${member.avatar_url}` : member.avatar_url) : "";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(member); }}
      data-testid={`member-avatar-${member.id}`}
      title="Profilbild vergrößern"
      className="w-10 h-10 rounded-full overflow-hidden border border-white/15 bg-white/[0.03] shrink-0 flex items-center justify-center hover:border-[#00e5ff]/60 transition-colors"
    >
      {src ? (
        <img src={src} alt={`${member.first_name} ${member.last_name}`} className="w-full h-full object-cover" />
      ) : (
        <span className="text-[11px] uppercase font-outfit font-bold text-white/80 tracking-wider">{initials || "??"}</span>
      )}
    </button>
  );
}

function MemberRow({ member, isLast, index, onOpenAvatar, currentUserId }) {
  const lvl = LEVEL_STYLES[member.ki_level];
  const isMe = currentUserId && member.id === currentUserId;
  return (
    <div
      className={`grid grid-cols-12 gap-4 px-5 sm:px-6 py-4 items-center transition-colors duration-200 hover:bg-white/[0.015] ${!isLast ? "border-b border-white/[0.05]" : ""} member-row-reveal`}
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
      data-testid={`member-row-${member.id}`}
    >
      <div className="col-span-12 sm:col-span-5 flex items-center gap-3 min-w-0">
        <div className="relative shrink-0">
          <MemberAvatar member={member} onClick={onOpenAvatar} />
          {isOnline(member.last_seen_at) && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-[#0a0a0a]" title="Online" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[15px] text-white font-outfit font-medium tracking-tight truncate">
              {member.first_name} {member.last_name}
              {isMe && <span className="text-[10px] text-white/35 font-normal uppercase tracking-wider ml-1">(du)</span>}
            </div>
            {member.founding_member && <FoundingBadge number={member.founding_number} compact size="sm" />}
          </div>
          {member.position && <div className="text-xs text-white/35 truncate mt-0.5">{member.position}</div>}
          {member.motto && (
            <div
              className="text-[11.5px] italic text-[#00e5ff]/70 truncate mt-0.5"
              title={member.motto}
              data-testid={`member-motto-${member.id}`}
            >
              „{member.motto}"
            </div>
          )}
        </div>
      </div>
      <div className="col-span-6 sm:col-span-4 flex items-center gap-2 min-w-0">
        {member.company ? (
          <>
            <Building2 size={13} className="text-white/30 shrink-0" />
            <span className="text-[14px] text-white/60 truncate">{member.company}</span>
          </>
        ) : (
          <span className="text-sm text-white/20">&mdash;</span>
        )}
      </div>
      <div className="col-span-6 sm:col-span-3 flex items-center justify-between gap-2">
        {member.ki_level && lvl ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] uppercase tracking-[0.15em] font-outfit font-semibold border" style={{ color: lvl.hex, background: lvl.bg, borderColor: lvl.border }}>
            <Sparkles size={10} />
            {member.ki_level}
          </span>
        ) : (
          <span className="text-sm text-white/20">&mdash;</span>
        )}
        {!isMe && (
          <Link
            to={`/chat?dm=${member.id}&name=${encodeURIComponent(`${member.first_name || ""} ${member.last_name || ""}`.trim())}&company=${encodeURIComponent(member.company || "")}`}
            data-testid={`member-chat-${member.id}`}
            title={`${member.first_name} direkt anschreiben`}
            className="shrink-0 w-9 h-9 flex items-center justify-center border border-[#00e5ff]/30 text-[#00e5ff]/80 hover:bg-[#00e5ff]/10 hover:text-[#00e5ff] hover:border-[#00e5ff]/70 transition-colors"
          >
            <MessageCircle size={14} />
          </Link>
        )}
      </div>
    </div>
  );
}

export default function MembersPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [previewMember, setPreviewMember] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/members`, { withCredentials: true });
      setMembers(data);
      setError("");
    } catch (err) {
      console.error("Failed to fetch members:", err);
      setError("Mitglieder konnten nicht geladen werden.");
    }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    if (!s) return members;
    return members.filter((m) =>
      `${m.first_name} ${m.last_name}`.toLowerCase().includes(s) ||
      (m.company || "").toLowerCase().includes(s) ||
      (m.position || "").toLowerCase().includes(s)
    );
  }, [members, search]);

  const levelCounts = useMemo(() => {
    const counts = { total: members.length };
    members.forEach((m) => { if (m.ki_level) counts[m.ki_level] = (counts[m.ki_level] || 0) + 1; });
    return counts;
  }, [members]);

  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 relative overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[45%] h-[55%] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.06) 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          {/* Hero */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-px bg-[#00e5ff]" />
                <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">Community</span>
              </div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white mb-3" data-testid="members-heading">
                <span className="text-[#00e5ff]">{members.length}</span> Mitglieder
              </h1>
              {(() => { const c = members.filter(m => isOnline(m.last_seen_at)).length; return c > 0 ? (
                <div className="flex items-center gap-2 mt-2">
                  <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" /></span>
                  <span className="text-[14px] font-outfit font-semibold text-green-400">{c} Mitglied{c > 1 ? "er" : ""} online</span>
                </div>
              ) : null; })()}
              <p className="text-base sm:text-lg text-white/50 leading-relaxed">
                Unternehmer aus dem Illertal, die KI verstehen und einsetzen wollen.
              </p>
            </div>
            <div className="relative shrink-0">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, Firma, Rolle suchen..." data-testid="members-search"
                className="pl-10 bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 h-11 w-full md:w-72 text-[14px]"
              />
            </div>
          </div>

          {/* Level breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.05] mb-8 border border-white/[0.06]">
            {[
              { key: "Einsteiger" },
              { key: "Fortgeschritten" },
              { key: "Profi" },
              { key: "Dienstleister" },
            ].map((l) => {
              const st = LEVEL_STYLES[l.key];
              return (
                <div key={l.key} className="bg-black px-4 sm:px-5 py-4 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles size={12} style={{ color: st.hex }} strokeWidth={1.6} />
                    <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-white/50 font-outfit font-medium truncate">{l.key}</span>
                  </div>
                  <div className="font-outfit font-black text-2xl sm:text-3xl tracking-tighter" style={{ color: st.hex }}>
                    {levelCounts[l.key] || 0}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Members list */}
          <div className="bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.06]" data-testid="members-list">
            <div className="grid grid-cols-12 gap-4 px-5 sm:px-6 py-3 border-b border-white/[0.06] text-[11px] text-white/45 uppercase tracking-[0.2em] font-outfit font-semibold bg-white/[0.015]">
              <div className="col-span-12 sm:col-span-5">Name</div>
              <div className="hidden sm:block sm:col-span-4">Unternehmen</div>
              <div className="hidden sm:block sm:col-span-3">KI-Level</div>
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <Users size={28} className="text-white/15 mx-auto mb-3" strokeWidth={1.3} />
                <p className="text-white/40 text-[15px]">Keine Mitglieder gefunden</p>
              </div>
            )}
            {filtered.map((m, idx) => (
              <MemberRow key={m.id} member={m} isLast={idx === filtered.length - 1} index={idx} onOpenAvatar={setPreviewMember} currentUserId={user?._id} />
            ))}
          </div>
          {error && <p className="mt-4 text-red-400/80 text-[13px]">{error}</p>}
        </div>
      </div>

      {previewMember && (
        <AvatarLightbox
          src={previewMember.avatar_url}
          name={`${previewMember.first_name} ${previewMember.last_name}`}
          subline={[previewMember.position, previewMember.company].filter(Boolean).join(" · ")}
          onClose={() => setPreviewMember(null)}
          chatHref={(user?._id && previewMember.id !== user._id) ? `/chat?dm=${previewMember.id}&name=${encodeURIComponent(`${previewMember.first_name || ""} ${previewMember.last_name || ""}`.trim())}&company=${encodeURIComponent(previewMember.company || "")}` : null}
        />
      )}

      <style>{`
        .member-row-reveal {
          opacity: 0;
          transform: translateX(-8px);
          animation: row-slide 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes row-slide { to { opacity: 1; transform: translateX(0); } }
        @media (prefers-reduced-motion: reduce) {
          .member-row-reveal { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}
